import { DateHelperService } from '../../services/date-helper.service';

/**
 * Date arithmetic behind the route timeline, kept out of the component so it can be tested
 * without booting Formly. Both helpers work in LOCAL time on purpose: the arrival-time values
 * are local wall-clock times, and parsing "2026-09-09" through `new Date()` would read it as
 * UTC and land on the previous day for anyone west of Greenwich.
 */
export const RouteDateUtil = {
  /** Parse a `YYYY-MM-DD` date part into a local midnight Date. */
  localDateFromISODate(isoDate: string): Date {
    const [year, month, day] = isoDate.split('-').map(Number);
    return new Date(year, month - 1, day);
  },

  /**
   * Add whole calendar days to `date` until it is no longer before `floor`.
   *
   * The estimate counts CALENDAR days rather than dividing the gap by 86_400_000ms. A calendar
   * day is not always 24h, so a fixed-day division overshoots by a whole day around the autumn
   * transition — measured over a year of half-hour starts: 150 such cases with `ceil`, 6 with
   * `floor`, 0 when counting calendar days. Normalising both sides to midnight makes the
   * division exact, so at most one further step is ever needed, never a loop over years.
   *
   * The spring transition has no minimal answer: 02:00–03:00 does not exist that night, so
   * adding whole days to an 02:00 stop can never land on 03:00 that day. It lands the day
   * after, which is the closest reachable moment.
   */
  /**
   * Whole calendar days from `from` to `to`, counted on the calendar rather than in milliseconds.
   *
   * Dividing the gap by 86_400_000 is off by one on the two DST days of the year, which is exactly
   * when a route is most likely to span midnight.
   */
  calendarDaysBetween(from: Date, to: Date): number {
    const midnight = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    return Math.round((midnight(to) - midnight(from)) / 86_400_000);
  },

  advanceToAtLeast(date: Date, floor: Date): Date {
    if (date.getTime() >= floor.getTime()) {
      return date;
    }
    const midnight = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const wholeDays = Math.round((midnight(floor).getTime() - midnight(date).getTime()) / 86_400_000);
    let result = wholeDays > 0 ? DateHelperService.addDays(date, wholeDays) : new Date(date);
    while (result.getTime() < floor.getTime()) {
      result = DateHelperService.addDays(result, 1);
    }
    return result;
  },

  /**
   * Resolve one stop's arrival datetime — the rule the whole route timeline runs on.
   *
   * `arrivalTimeValue` is what the control holds: a full datetime even when the field shows
   * only a time. Its date part is either an explicit entry or what an earlier run of this
   * calculation wrote, and it is honoured. Only a value that would land before
   * `previousArrival` is pushed forward, so "22:00 then 01:00" still becomes the next day
   * while a stored date is never pulled back.
   *
   * The previous implementation compared CLOCK TIMES instead. It read 10:00 the day after
   * tomorrow as "not before 08:00", derived no day change, and wrote the stop back onto day 1
   * — silently discarding the entered date. That is what made sorting a no-op and made a
   * visible date field impossible to build on.
   *
   * With `trustStoredDate` the forward push is skipped entirely — see the comment at the
   * return for why the two regimes exist.
   *
   * Returns null when the value carries no usable time (empty, unparseable, or the date-only
   * seed `add()` writes for a new stop); the caller leaves such a stop out of the calculation.
   */
  deriveArrival(
    arrivalTimeValue: string | null | undefined,
    previousArrival: Date,
    trustStoredDate = false
  ): { arrival: Date; time: string } | null {
    const time = DateHelperService.extractTimeFromDateTime(arrivalTimeValue);
    if (!time) {
      return null;
    }
    const parsed = DateHelperService.parseTimeString(time);
    if (!parsed) {
      return null;
    }

    // A usable time implies a date part: dateTimeToDateAndTime only ever returns them
    // together, and the date-only branch yields no time at all.
    const storedDatePart = DateHelperService.dateTimeToDateAndTime(arrivalTimeValue).date;
    const arrival = RouteDateUtil.localDateFromISODate(storedDatePart);
    arrival.setHours(parsed.hours, parsed.minutes, 0, 0);

    // With the date on screen the planner owns it: deriving a day from the order would fight
    // the value they just typed, pushing 09-09 to 10-09 the moment it looks "too early". The
    // route sorts itself on these datetimes instead, so an out-of-order stop moves rather than
    // gets rewritten. Time-only keeps the old rule: "22:00 then 01:00" is the next day, because
    // the order is the only signal there is.
    if (trustStoredDate) {
      return { arrival, time };
    }

    return { arrival: RouteDateUtil.advanceToAtLeast(arrival, previousArrival), time };
  },
};
